/**
 * Per-event match length (sets / points) for Host Tournament categories.
 * Distinct from casual Arena defaults in sportMatchFormatCatalog.
 */

import { normalizePointsToWin } from "../sport/sportMatchFormatCatalog";
import { DEFAULT_SPORT, SPORT_IDS, normalizeSportId } from "../sport/sportTypes";

function formatSetsLabelSimple(setsToWin) {
  const n = Number(setsToWin);
  if (!Number.isFinite(n) || n < 1) return "1 Set";
  if (n <= 1) return "1 Set";
  return `Best of ${n * 2 - 1}`;
}

/**
 * @typedef {{ setsToWin: number, pointsToWin: number }} EventMatchFormat
 */

/**
 * Host tournament defaults: Best of 1 set; 15 pts badminton, 11 pts table tennis.
 * @param {string | null | undefined} sportId
 * @returns {EventMatchFormat}
 */
export function getDefaultEventMatchFormatForSport(sportId) {
  const id = normalizeSportId(sportId);
  if (id === SPORT_IDS.TABLE_TENNIS) {
    return { setsToWin: 1, pointsToWin: 11 };
  }
  if (id === SPORT_IDS.BADMINTON) {
    return { setsToWin: 1, pointsToWin: 15 };
  }
  return { setsToWin: 1, pointsToWin: 21 };
}

/**
 * @param {unknown} input
 * @param {string | null | undefined} [sportId]
 * @returns {EventMatchFormat}
 */
export function normalizeEventMatchFormat(input, sportId = DEFAULT_SPORT) {
  const defaults = getDefaultEventMatchFormatForSport(sportId);
  if (!input || typeof input !== "object") return { ...defaults };
  const setsRaw = Number(input.setsToWin);
  const setsToWin =
    Number.isFinite(setsRaw) && setsRaw >= 1 && setsRaw <= 6
      ? Math.trunc(setsRaw)
      : defaults.setsToWin;
  const pointsToWin = normalizePointsToWin(input.pointsToWin, defaults.pointsToWin);
  return { setsToWin, pointsToWin };
}

/**
 * @param {EventMatchFormat | null | undefined} format
 * @returns {string}
 */
export function formatEventMatchFormatLine(format) {
  if (!format) return "";
  const sets = Number(format.setsToWin);
  const pts = Number(format.pointsToWin);
  if (!Number.isFinite(sets) || !Number.isFinite(pts)) return "";
  const lengthLabel = sets <= 1 ? "1 set" : formatSetsLabelSimple(sets).toLowerCase();
  return `${lengthLabel} · ${pts} pts`;
}

/**
 * @param {import('./eventCompetition').EventCompetition | null | undefined} competition
 * @param {string | null | undefined} [sportId]
 * @returns {EventMatchFormat}
 */
export function resolveEventMatchFormat(competition, sportId = DEFAULT_SPORT) {
  return normalizeEventMatchFormat(competition?.matchFormat, sportId);
}

/**
 * Resolve sets/points for launching a managed fixture into Match Court.
 * Prefer event Host Tournament matchFormat, then arena defaultMatchFormat, then sport event defaults.
 * @param {object | null | undefined} arena
 * @param {{ eventId?: string | null, sportId?: string | null }} [opts]
 * @returns {EventMatchFormat}
 */
export function resolveArenaPlayMatchFormat(arena, opts = {}) {
  const sportId = normalizeSportId(
    opts.sportId || arena?.sportId || arena?.sport || DEFAULT_SPORT
  );
  const eventId = opts.eventId ? String(opts.eventId) : "";
  const events = arena?.managedEvents || [];
  const event = eventId
    ? events.find((e) => e && e.eventId === eventId)
    : null;
  if (event) {
    const fromEvent = resolveEventMatchFormat(
      event.competition || event,
      sportId
    );
    if (fromEvent?.setsToWin != null && fromEvent?.pointsToWin != null) {
      return fromEvent;
    }
  }

  const dm = arena?.settings?.defaultMatchFormat;
  if (dm && (dm.setsToWin != null || dm.pointsToWin != null)) {
    return normalizeEventMatchFormat(dm, sportId);
  }

  const legacySets = arena?.settings?.setsToWin ?? arena?.setsToWin;
  const legacyPts = arena?.settings?.pointsToWin ?? arena?.pointsToWin;
  if (legacySets != null || legacyPts != null) {
    return normalizeEventMatchFormat(
      { setsToWin: legacySets, pointsToWin: legacyPts },
      sportId
    );
  }

  return getDefaultEventMatchFormatForSport(sportId);
}

/**
 * Hero / summary line for competition-level match length.
 * @param {object | null | undefined} competition
 * @returns {string | null}
 */
export function resolveCompetitionHeroMatchFormatHint(competition) {
  if (!competition) return null;
  const sportId = normalizeSportId(competition.sportId || competition.sport);
  const events = competition.managedEvents || [];
  const lines = events
    .map((ev) => formatEventMatchFormatLine(resolveEventMatchFormat(ev.competition, sportId)))
    .filter(Boolean);
  const unique = [...new Set(lines)];
  if (unique.length === 1) return unique[0];
  if (unique.length > 1) return "Varies by category";
  const settings = competition.settings?.defaultMatchFormat;
  if (settings?.setsToWin != null && settings?.pointsToWin != null) {
    return formatEventMatchFormatLine(settings);
  }
  return formatEventMatchFormatLine(getDefaultEventMatchFormatForSport(sportId));
}
